/* ============================================================
   Wyld Chics — cart drawer behaviour
   assets/wc-cart.js

   Three jobs, all delegated because [data-cart-items] is replaced by
   Alpine.morph on every cart change. Listeners are CAPTURE phase:
   verified live that bubble-phase listeners on document never fire
   inside this drawer.

   1. [data-wc-add="<variant id>"]  add one (upsell tiles, empty state)
   2. [data-wc-code] form            apply a discount code through
                                     /cart/update.js { discount } (Cart
                                     AJAX API, May 2025); chips with
                                     [data-wc-code-remove] take one off
   3. free gift                      [data-wc-tiers] carries
                                     data-gift-variant / -unlocked / -key.
                                     Unlocked and missing -> add it with
                                     property _wc_gift. Locked and present
                                     -> remove it. If the shopper removes
                                     it themselves we stop re-adding until
                                     the tier locks again.

   Every write asks for the theme's sections and then dispatches the
   theme's own event so drawer, footer and header count refresh
   (same contract as island-quick-buy.bundle.js).
   ============================================================ */
(function () {
  'use strict';

  var busy = false;
  var SECTIONS = 'cart-items,cart-footer,cart-item-count';
  var HEADERS = { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' };

  function routes() { return (window.theme && window.theme.routes) || {}; }

  function fire(name, data) {
    document.body.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { response: data } }));
  }

  function addVariant(variantId, properties) {
    var body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    if (properties) {
      Object.keys(properties).forEach(function (k) { body.append('properties[' + k + ']', properties[k]); });
    }
    body.append('sections', SECTIONS);
    body.append('sections_url', window.location.pathname);
    return fetch(routes().cart_add_url || '/cart/add', { method: 'POST', body: body, headers: HEADERS })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.status) throw new Error(data.description || data.message || 'add failed');
        fire('shapes:cart:afteradditem', data);
        return data;
      });
  }

  function changeLine(key, quantity) {
    var body = new FormData();
    body.append('id', key);
    body.append('quantity', String(quantity));
    body.append('sections', SECTIONS);
    body.append('sections_url', window.location.pathname);
    return fetch(routes().cart_change_url || '/cart/change', { method: 'POST', body: body, headers: HEADERS })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.status) throw new Error(data.description || data.message || 'change failed');
        fire('shapes:cart:cartqtychange', data);
        return data;
      });
  }

  /* /cart/update.js does not take `sections`; after it we ask the theme
     to re-fetch everything (theme-events.js listens for this). */
  function setDiscounts(codes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    }).then(function (r) { return r.json(); });
  }
  function refreshAll() { document.dispatchEvent(new CustomEvent('theme:update:cart')); }

  /* ---------- 1. add buttons ---------- */
  document.addEventListener('click', function (evt) {
    var btn = evt.target && evt.target.closest ? evt.target.closest('[data-wc-add]') : null;
    if (!btn || busy) return;
    evt.preventDefault();
    var variantId = btn.getAttribute('data-wc-add');
    if (!variantId) return;
    busy = true;
    btn.setAttribute('aria-busy', 'true');
    addVariant(variantId)
      .catch(function (err) {
        btn.removeAttribute('aria-busy');
        if (window.console) console.warn('[wc-cart] add failed:', err && err.message);
      })
      .then(function () { busy = false; });
  }, true);

  /* ---------- 2. discount codes ---------- */
  /* The codes on the cart come from /cart.js, never from Liquid: a free-shipping code
     (WYLDFREE) discounts nothing in the cart, so Liquid cannot see it, and building the
     list from the rendered chips dropped it whenever another code was removed. */
  /* Each attached code with: applicable, and whether the cart shows a discount for it. An
     applicable code that discounts nothing in the cart can only be a shipping code. Decided
     from the one /cart.js response, never from the drawer DOM (which can be a stale render). */
  function attachedCodeList() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var seen = {};
        (c.cart_level_discount_applications || []).forEach(function (d) { if (d.type === 'discount_code') seen[String(d.title).toUpperCase()] = true; });
        (c.items || []).forEach(function (i) { (i.line_level_discount_allocations || []).forEach(function (a) { var d = a.discount_application || {}; if (d.type === 'discount_code') seen[String(d.title).toUpperCase()] = true; }); });
        return (c.discount_codes || []).map(function (d) { return { code: d.code, applicable: !!d.applicable, shipping: !!d.applicable && !seen[String(d.code).toUpperCase()] }; });
      })
      .catch(function () { return []; });
  }
  function currentCodes() {
    return attachedCodeList().then(function (list) { return list.map(function (d) { return d.code; }); });
  }

  /* the field is not a <form> (it lives inside the theme's cart form), so:
     click on Apply, or Enter in the input */
  /* Shopify reports a code as not applicable both when it is wrong and
     when the cart already carries automatic offers worth more (it keeps the
     bigger one; codes and Buy X get Y do not stack on Basic). The Cart API
     cannot tell the two apart, so: automatic offers applied -> "you already
     have the better deal" note; none -> the plain error. Jonathan, 2026-09-12:
     nobody should feel cheated by "invalid code". */
  function hasAutomatic(cart) {
    var lines = (cart && cart.items) || [];
    for (var i = 0; i < lines.length; i++) {
      var allocs = lines[i].line_level_discount_allocations || [];
      for (var j = 0; j < allocs.length; j++) {
        var app = allocs[j].discount_application || {};
        if (app.type === 'automatic' && allocs[j].amount > 0) return true;
      }
    }
    var apps = (cart && cart.cart_level_discount_applications) || [];
    for (var k = 0; k < apps.length; k++) if (apps[k].type === 'automatic') return true;
    return false;
  }
  function applyCode(form) {
    if (busy) return;
    var input = form.querySelector('[data-wc-code-input]');
    var error = form.querySelector('[data-wc-code-error]');
    var note = form.querySelector('[data-wc-code-outranked]');
    var code = (input.value || '').trim();
    if (!code) return;
    busy = true;
    form.setAttribute('aria-busy', 'true');
    error.hidden = true;
    if (note) note.hidden = true;
    var conflictEl = form.querySelector('[data-wc-code-conflict]'); if (conflictEl) conflictEl.hidden = true;
    var codes = [];
    var conflict = form.querySelector('[data-wc-code-conflict]');
    function say(el, text) { if (!el) return; el.textContent = text; el.hidden = false; }
    function sayAfterRender(text) { pendingNote = text; }   // the re-render replaces the note element; syncCodes puts it back
    currentCodes()
      .then(function (list) {
        codes = list;
        if (codes.some(function (c) { return c.toLowerCase() === code.toLowerCase(); })) { input.value = ''; form.removeAttribute('aria-busy'); return null; }
        return setDiscounts(codes.concat(code));
      })
      .then(function (cart) {
        if (!cart) return;
        var all = cart.discount_codes || [];
        var entry = all.filter(function (d) { return d.code.toLowerCase() === code.toLowerCase(); })[0];
        var others = all.filter(function (d) { return d.code.toLowerCase() !== code.toLowerCase(); });
        var otherOn = others.filter(function (d) { return d.applicable; }).map(function (d) { return d.code.toUpperCase(); });
        var otherOff = others.filter(function (d) { return !d.applicable; }).map(function (d) { return d.code; });
        if (!entry || !entry.applicable) {
          /* Refused. Three reasons Shopify does not tell apart: a bad code, a code beaten by
             another code already on the cart (they never stack), or a code worth less than
             the automatic "$15 off" (Alia's small codes). Say the most likely one. */
          if (otherOn.length) say(conflict, (form.getAttribute('data-msg-conflict') || '').replace('{code}', code.toUpperCase()).replace('{other}', otherOn[0]));
          else error.hidden = false;   // codes stack with the $15 (2026-09-15), so a refused lone code is a bad code
          void note;
          form.removeAttribute('aria-busy');
          return setDiscounts(codes);   // put the cart back the way it was
        }
        /* Applied, and it beat a code that was on before: detach the loser (Shopify would keep
           it attached but dead) and say so, instead of a struck-through chip with no explanation. */
        if (otherOff.length) {
          var keep = all.filter(function (d) { return d.applicable; }).map(function (d) { return d.code; });
          sayAfterRender((form.getAttribute('data-msg-replaced') || '').replace('{code}', code.toUpperCase()).replace('{other}', otherOff[0].toUpperCase()));
          input.value = '';
          return setDiscounts(keep).then(function () { refreshAll(); });
        }
        input.value = '';
        refreshAll();
      })
      .catch(function (err) {
        /* Shopify sometimes answers a successful write with an error page ("upstream connect
           error"), so ask the cart what actually happened before blaming the code. */
        if (window.console) console.warn('[wc-cart] code failed:', err && err.message);
        return attachedCodeList().then(function (list) {
          var hit = list.filter(function (d) { return d.code.toLowerCase() === code.toLowerCase(); })[0];
          if (hit && hit.applicable) { input.value = ''; refreshAll(); return; }
          error.hidden = false;
          form.removeAttribute('aria-busy');
        });
      })
      .then(function () { busy = false; });
  }
  document.addEventListener('click', function (evt) {
    var b = evt.target && evt.target.closest ? evt.target.closest('[data-wc-code-apply]') : null;
    if (!b) return;
    evt.preventDefault();
    applyCode(b.closest('[data-wc-code]'));
  }, true);
  document.addEventListener('keydown', function (evt) {
    if (evt.key !== 'Enter') return;
    var i = evt.target && evt.target.closest ? evt.target.closest('[data-wc-code-input]') : null;
    if (!i) return;
    evt.preventDefault();          // never submit the theme's cart form
    applyCode(i.closest('[data-wc-code]'));
  }, true);

  document.addEventListener('click', function (evt) {
    var x = evt.target && evt.target.closest ? evt.target.closest('[data-wc-code-remove]') : null;
    if (!x || busy) return;
    evt.preventDefault();
    var drop = x.getAttribute('data-wc-code-remove').toLowerCase();
    busy = true;
    x.closest('.wc-tag').setAttribute('aria-busy', 'true');
    currentCodes()
      .then(function (list) { return setDiscounts(list.filter(function (c) { return c.toLowerCase() !== drop; })); })
      .then(function () { refreshAll(); })
      .catch(function (err) { if (window.console) console.warn('[wc-cart] code remove failed:', err && err.message); })
      .then(function () { busy = false; });
  }, true);

  /* After every render: chips for codes Liquid could not see (a free-shipping code has no
     allocation in the cart), a struck-through chip for a code that is attached but not
     applicable right now, and the shipping milestone lit while a shipping code is on. A
     code that is applicable and unknown to Liquid can only be a shipping code. */
  var codeSyncSeq = 0;
  var pendingNote = '';
  function syncCodes() {
    var form = document.querySelector('[data-cart-drawer] [data-wc-code]');
    if (!form) return;
    var seq = ++codeSyncSeq;
    attachedCodeList().then(function (list) {
      if (seq !== codeSyncSeq) return;                     // a newer render already ran
      var form2 = document.querySelector('[data-cart-drawer] [data-wc-code]');
      var box = form2 && form2.querySelector('[data-wc-code-chips]');
      if (!box) return;
      var shipCode = '';
      list.forEach(function (d) {
        var up = d.code.toUpperCase();
        var chip = box.querySelector('[data-wc-chip="' + up.replace(/"/g, '') + '"]');
        if (!chip) {
          chip = document.createElement('span');
          chip.className = 'wc-tag wc-tag--code';
          chip.setAttribute('data-wc-chip', up);
          chip.textContent = up;
          var b = document.createElement('button');
          b.type = 'button'; b.className = 'wc-tag__x'; b.setAttribute('data-wc-code-remove', d.code); b.setAttribute('aria-label', 'Remove code ' + d.code); b.innerHTML = '&times;';
          chip.appendChild(b);
          box.appendChild(chip);
        }
        chip.classList.toggle('is-off', !d.applicable);
        if (d.shipping && !shipCode) shipCode = d.code;
      });
      box.hidden = !box.querySelector('[data-wc-chip]');
      if (pendingNote) { var n = form2.querySelector('[data-wc-code-conflict]'); if (n) { n.textContent = pendingNote; n.hidden = false; } pendingNote = ''; }
      if (shipCode) markShippingCode(shipCode);
    });
  }
  function markShippingCode(code) {
    var ship = document.querySelector('[data-cart-drawer] [data-wc-ship-value]');
    if (ship) ship.textContent = ship.getAttribute('data-free-label') || 'FREE SHIPPING';
    var el = document.querySelector('[data-cart-drawer] [data-wc-tiers]');
    if (!el) return;
    var stops = el.querySelectorAll('.wc-tiers__stop');
    var n = stops.length, idx = -1;
    stops.forEach(function (li, i) { if (li.getAttribute('data-type') === 'shipping') idx = i; });
    if (idx < 0) return;
    var li = stops[idx];
    if (li.classList.contains('is-hit')) return;             // the total already earns it
    li.classList.add('is-hit');
    var text = el.querySelector('.wc-tiers__text');
    var msg = el.getAttribute('data-msg-ship-code') || '';
    if (text && msg) text.textContent = msg.replace('{code}', code);
    var bar = el.querySelector('.wc-tiers__fill');
    var centre = (idx + 0.5) * 100 / n;
    if (bar && (parseFloat(bar.style.width) || 0) < centre) bar.style.width = centre + '%';
  }
  ['shapes:cart:afteradditem', 'shapes:cart:cartqtychange', 'shapes:cart:update'].forEach(function (name) {
    window.addEventListener(name, function () { setTimeout(syncCodes, 30); });
  });
  document.addEventListener('theme:cart-drawer:opening', function () { setTimeout(syncCodes, 150); });
  setTimeout(syncCodes, 600);

  /* ---------- merged rows: one stepper for a line Shopify split ---------- */
  /* cart-items.liquid renders one row per variant and lists every piece's
     key in data-wc-keys. The theme's CartItem posts /cart/change for ONE
     key, which is how the plus button grew a duplicate row. For a row with
     several keys we take over the change event in the capture phase (so
     Alpine's handler never runs) and set them all in one /cart/update.js:
     the first piece gets the new total, the others go to zero. */
  /* Every row, not only merged ones: the theme's CartItem keeps the key it
     was born with, and Shopify re-keys lines on each write once discounts
     split them, so its /cart/change could hit a key that no longer exists
     ("no valid id or line parameter", Jonathan 2026-09-13). data-wc-keys
     is fresh on every render. */
  document.addEventListener('change', function (evt) {
    var input = evt.target;
    if (!input || input.name !== 'updates[]' || !input.closest) return;
    var li = input.closest('[data-wc-keys]');
    if (!li || !li.closest('[data-cart-drawer]')) return;
    var keys = (li.getAttribute('data-wc-keys') || '').split(',').filter(Boolean);
    if (!keys.length) return;
    evt.stopPropagation();
    var last = input.getAttribute('data-last-value');
    var raw = String(input.value || '').trim();
    /* a cleared or half-typed field is not "zero": the theme's keyup debounce fires change
       750ms after the shopper empties the box to type a new number, and 0 would delete the line */
    if (raw === '' || isNaN(parseInt(raw, 10))) { if (last !== null) input.value = last; return; }
    if (busy) { if (last !== null) input.value = last; return; }   // a code write is in flight: drop this change, keep the row honest
    var qty = Math.max(0, parseInt(raw, 10) || 0);
    if (last !== null && qty === parseInt(last, 10)) return;
    var updates = {};
    keys.forEach(function (k, i) { updates[k] = i === 0 ? qty : 0; });
    busy = true;
    li.setAttribute('aria-busy', 'true');
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (r) { return r.json(); })
      .then(function () { refreshAll(); })
      .catch(function (err) { li.removeAttribute('aria-busy'); if (window.console) console.warn('[wc-cart] merged update failed:', err && err.message); })
      .then(function () { busy = false; });
  }, true);

  /* ---------- drawer height follows the visible viewport ---------- */
  /* iOS Chrome collapses its bottom toolbar while the page is scroll-locked
     and leaves the fixed layer at the old height: the drawer ended above the
     screen edge with the page showing through (Jonathan, 2026-09-19). The
     visual viewport is what the shopper actually sees, so wc-cart.css sizes
     the modal from these two variables. Never while a field in the drawer
     has focus (the keyboard), never while pinch-zoomed. */
  function fieldFocusedInDrawer() {
    var ae = document.activeElement;
    return !!(ae && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) && ae.closest && ae.closest('#right-drawer-slot, [data-cart-drawer]'));
  }
  function setViewportVars() {
    /* keyboard up for the code field: the visible area shrinks exactly like a
       toolbar collapse, and sizing the drawer to it squeezed the whole cart
       above the keyboard ("pushes everything up", Jonathan 2026-09-19). Leave
       the drawer alone while a field in it has focus; iOS scrolls the field
       into view on its own, as it did before. */
    if (fieldFocusedInDrawer()) return;
    var vv = window.visualViewport;
    if (vv && vv.scale && Math.abs(vv.scale - 1) > 0.01) return;   // pinch-zoomed: the visual viewport is a crop, not the screen
    var h = vv ? vv.height : window.innerHeight;
    var t = vv ? vv.offsetTop : 0;
    if (!(h > 200)) return;   // hidden, prerendered or unpainted tab reports 0: keep the CSS fallback (100dvh) instead of a 0px drawer
    var root = document.documentElement.style;
    root.setProperty('--wc-vvh', Math.round(h) + 'px');
    root.setProperty('--wc-vvt', Math.round(t) + 'px');
  }
  setViewportVars();
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', setViewportVars);
    window.visualViewport.addEventListener('scroll', setViewportVars);
  }
  window.addEventListener('resize', setViewportVars);
  window.addEventListener('orientationchange', setViewportVars);
  document.addEventListener('focusout', function () { setTimeout(setViewportVars, 400); });   // keyboard gone: size to the screen again

  /* ---------- Afterpay messaging library for the drawer ---------- */
  /* The Afterpay app only loads js.squarecdn.com/square-marketplace.js on
     product pages (its app block). The drawer's <square-placement> under
     Subtotal needs it everywhere, so load it once when a placement exists. */
  var afterpayLoading = false;
  function ensureAfterpay() {
    if (afterpayLoading || (window.customElements && customElements.get('square-placement'))) return;
    if (!document.querySelector('[data-wc-afterpay] square-placement')) return;
    if (document.querySelector('script[src*="square-marketplace.js"]')) { afterpayLoading = true; return; }
    afterpayLoading = true;
    var s = document.createElement('script');
    s.src = 'https://js.squarecdn.com/square-marketplace.js';
    s.async = true;
    document.body.appendChild(s);
  }
  if (document.readyState === 'complete') setTimeout(ensureAfterpay, 0);
  else window.addEventListener('load', function () { setTimeout(ensureAfterpay, 0); });
  document.addEventListener('theme:update:cart', function () { setTimeout(ensureAfterpay, 300); });

  /* ---------- body.wc-cart-open (hides the Inbox chat bubble) ---------- */
  function watchDrawer() {
    if (!window.Alpine || !Alpine.store || !Alpine.effect) return;
    Alpine.effect(function () {
      var m = Alpine.store('modals');
      var open = !!(m && m.rightDrawer && m.rightDrawer.open && m.rightDrawer.contents === 'cart');
      document.body.classList.toggle('wc-cart-open', open);
      if (open) { setViewportVars(); ensureAfterpay(); }
    });
    /* the header badge pops when the count goes up (Jonathan, 2026-09-15: people
       could not tell how many items were in the cart without opening it) */
    var lastCount = null;
    Alpine.effect(function () {
      var c = Alpine.store('cart_count');
      var n = c ? Number(c.count) : 0;
      if (lastCount !== null && n > lastCount) {
        document.querySelectorAll('[data-wc-cart-badge]').forEach(function (b) {
          b.classList.remove('is-pop'); void b.offsetWidth; b.classList.add('is-pop');
          setTimeout(function () { b.classList.remove('is-pop'); }, 700);
        });
      }
      lastCount = n;
    });
  }
  if (window.Alpine) watchDrawer(); else document.addEventListener('alpine:initialized', watchDrawer);

  /* ---------- 3. free gift ---------- */
  var giftBusy = false;
  function syncGift() {
    if (giftBusy || busy) return;
    var el = document.querySelector('[data-wc-tiers]');
    if (!el) return;
    var variant = el.getAttribute('data-gift-variant');
    var unlocked = el.getAttribute('data-gift-unlocked') === 'true';
    var key = el.getAttribute('data-gift-key');
    var manualKey = el.getAttribute('data-gift-manual-key');   // the shopper added the gift product themselves
    var codes = el.getAttribute('data-codes') || '';
    var codeWins = el.getAttribute('data-code-wins') === 'true';
    // a free line from an earlier rule (different product): always take it out
    var stale = (el.getAttribute('data-stale-keys') || '').split(',').filter(Boolean);
    if (stale.length) {
      giftBusy = true;
      changeLine(stale[0], 0)
        .catch(function (err) { if (window.console) console.warn('[wc-cart] stale gift remove failed:', err && err.message); })
        .then(function () { giftBusy = false; });
      return;   // the morph re-runs syncGift for anything else
    }
    if (!variant) return;
    var store; try { store = window.sessionStorage; } catch (e) { store = null; }
    var dismissed = store && store.getItem('wc_gift_dismissed') === variant;
    var added = store && store.getItem('wc_gift_added') === variant;

    /* a code Shopify rated above the offers: Liquid sees it on the render
       where the gift line is not $0 (data-code-wins). We take the line out
       and remember the code, so it is not added back while that code is on;
       later renders cannot see the paid line any more, so the bar is put
       into the same "code wins" state from here. */
    var blocked = store ? store.getItem('wc_gift_blocked') : null;
    if (codeWins && codes) { if (store) store.setItem('wc_gift_blocked', codes); blocked = codes; }
    if (blocked && blocked !== codes) { if (store) store.removeItem('wc_gift_blocked'); blocked = null; }
    if (blocked) {
      showCodeWins(el);
      if (store) { store.removeItem('wc_gift_added'); store.removeItem('wc_gift_dismissed'); }   // our removal is not the shopper's
      if (key) {
        giftBusy = true;
        /* remember which codes were on the cart when we pulled the gift. With
           6 lashes + WYLD20, the offers are worth more WITHOUT the glue
           (two lashes free) and the code is worth more WITH it, so adding
           the glue made the code win, pulling it made the offers win, and
           the cart flipped forever (Jonathan, 2026-09-13). The gift stays out
           until the attached codes change. */
        attachedCodes().then(function (att) { if (store && att) store.setItem('wc_gift_flip', att); });
        changeLine(key, 0).catch(function () {}).then(function () { giftBusy = false; });
      }
      return;
    }

    if (unlocked && !key) {
      if (manualKey) return;              // their own line becomes the free one (Shopify discounts the cheapest eligible line)
      if (added) {                        // we put it in once and it is gone: the shopper took it out
        if (store) store.setItem('wc_gift_dismissed', variant);
        return;
      }
      if (dismissed) return;
      giftBusy = true;
      attachedCodes().then(function (att) {
        var flip = store ? store.getItem('wc_gift_flip') : null;
        if (flip && flip === att) return null;                       // adding it back would restart the ping-pong
        if (flip && store) store.removeItem('wc_gift_flip');           // codes changed: fresh start
        return addVariant(variant, { _wc_gift: '1' })
          .then(function () { if (store) store.setItem('wc_gift_added', variant); });
      })
        .catch(function (err) { if (window.console) console.warn('[wc-cart] gift add failed:', err && err.message); })
        .then(function () { giftBusy = false; });
    } else if (!unlocked && key) {
      giftBusy = true;
      changeLine(key, 0)
        .catch(function (err) { if (window.console) console.warn('[wc-cart] gift remove failed:', err && err.message); })
        .then(function () { giftBusy = false; });
    } else if (!unlocked && store) {
      store.removeItem('wc_gift_added');    // locked again: next unlock may add it again
      store.removeItem('wc_gift_dismissed');
    }
  }
  /* every code attached to the cart, applied or refused (Liquid only sees applied ones) */
  function attachedCodes() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { return (c.discount_codes || []).map(function (d) { return d.code.toUpperCase(); }).sort().join(','); })
      .catch(function () { return ''; });
  }

  function showCodeWins(el) {
    if (el.getAttribute('data-code-wins') === 'true') return;   // Liquid already rendered it
    var text = el.querySelector('.wc-tiers__text');
    var msg = el.getAttribute('data-msg-code-wins') || '';
    var first = (el.getAttribute('data-codes') || '').split(',')[0];
    if (text && msg) text.textContent = msg.replace('{code}', first);
    var stops = el.querySelectorAll('.wc-tiers__stop');
    var n = stops.length, fill = 0;
    stops.forEach(function (li, i) {
      if (li.getAttribute('data-type') === 'shipping') { if (li.classList.contains('is-hit')) fill = (i + 0.5) * 100 / n; return; }
      li.classList.remove('is-hit');   // both glyphs are in the DOM; CSS swaps the check back to the icon
    });
    var bar = el.querySelector('.wc-tiers__fill');
    if (bar) bar.style.width = fill + '%';
  }

  ['shapes:cart:afteradditem', 'shapes:cart:cartqtychange', 'shapes:cart:update'].forEach(function (name) {
    window.addEventListener(name, function () { setTimeout(syncGift, 60); });
  });
  // the drawer is teleported lazily, so also sync when it opens and when the
  // tiers node first appears (a leftover gift must go before anyone sees it)
  document.addEventListener('theme:cart-drawer:opening', function () { setTimeout(syncGift, 120); });
  if (window.MutationObserver) {
    var seen = false;
    new MutationObserver(function () {
      if (seen) return;
      if (document.querySelector('[data-wc-tiers]')) { seen = true; setTimeout(syncGift, 120); }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(syncGift, 400); });
  else setTimeout(syncGift, 400);
})();
